import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import BusinessCard from "@/components/BusinessCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, SlidersHorizontal, Loader2, Compass, MapPin } from "lucide-react";

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [states, setStates] = useState([]);
  const [tags, setTags] = useState([]);
  const [cities, setCities] = useState([]);
  const [results, setResults] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("search") || "");

  const parent = params.get("parent") || "";
  const category = params.get("category") || "";
  const state = params.get("state") || "";
  const city = params.get("city") || "";
  const tag = params.get("tag") || "";
  const sort = params.get("sort") || "rating";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/states").then(({ data }) => setStates(data));
    api.get("/tags").then(({ data }) => setTags(data.flatMap((g) => g.tags).slice(0, 40)));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = Object.fromEntries(params.entries());
    api.get("/businesses", { params: { ...p, limit: 100 } })
      .then(({ data }) => setResults(data))
      .finally(() => setLoading(false));
  }, [params]);

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (val && val !== "all") next.set(key, val); else next.delete(key);
    if (key === "parent") next.delete("category");
    if (key === "state") next.delete("city");
    setParams(next);
  };

  useEffect(() => {
    if (!state) { setCities([]); return; }
    api.get("/cities", { params: { state } }).then(({ data }) => setCities(data));
  }, [state]);

  // Nearby fallback: a city is filtered but nothing matched
  useEffect(() => {
    if (loading || results.length > 0 || !state || !city) { setNearby([]); return; }
    api.get("/cities/nearby", { params: { state, city } })
      .then(({ data }) => setNearby(data.suggestions || []))
      .catch(() => setNearby([]));
  }, [loading, results, state, city]);

  const cityMeta = useMemo(() => Object.fromEntries(cities.map((c) => [c.slug, c])), [cities]);

  // Metro grouping: when a state is chosen but no specific city, roll results up by metro market
  const metroGroups = useMemo(() => {
    if (!state || city || results.length === 0) return null;
    const map = new Map();
    for (const b of results) {
      const meta = cityMeta[b.city_slug];
      const key = meta?.metro_slug || b.city_slug || "other";
      if (!map.has(key)) map.set(key, { name: meta?.metro || b.city || "Other", slug: meta?.metro_slug || "", items: [] });
      map.get(key).items.push(b);
    }
    if (map.size <= 1) return null;
    return [...map.values()].sort((a, b) => b.items.length - a.items.length);
  }, [state, city, results, cityMeta]);

  const activeParent = useMemo(() => cats.find((c) => c.slug === parent), [cats, parent]);

  const submitSearch = () => update("search", q);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-6 rounded-2xl bg-card border border-border p-2 shadow-sm">
        <div className="flex-1 flex items-center gap-2 px-2">
          <Search className="h-5 w-5 text-sam-slate" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitSearch()}
            placeholder="Search businesses…" data-testid="browse-search-input" className="border-0 shadow-none focus-visible:ring-0" />
        </div>
        <Button className="bg-sam-navy hover:bg-sam-navy2" onClick={submitSearch} data-testid="browse-search-btn">Search</Button>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Filters */}
        <aside className="space-y-6">
          <div className="flex items-center gap-2 font-display font-bold text-sam-navy"><SlidersHorizontal className="h-4 w-4" />Filters</div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-sam-slate mb-2">Category</div>
            <Select value={parent || "all"} onValueChange={(v) => update("parent", v)}>
              <SelectTrigger data-testid="filter-parent"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All categories</SelectItem>
                {cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {activeParent && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-sam-slate mb-2">Subcategory</div>
              <Select value={category || "all"} onValueChange={(v) => update("category", v)}>
                <SelectTrigger data-testid="filter-category"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All {activeParent.name}</SelectItem>
                  {activeParent.children.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-sam-slate mb-2">State</div>
            <Select value={state || "all"} onValueChange={(v) => update("state", v)}>
              <SelectTrigger data-testid="filter-state"><SelectValue placeholder="All states" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All states</SelectItem>
                {states.map((s) => <SelectItem key={s.abbr} value={s.abbr}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {state && cities.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-sam-slate mb-2">City</div>
              <Select value={city || "all"} onValueChange={(v) => update("city", v)}>
                <SelectTrigger data-testid="filter-city"><SelectValue placeholder="All cities" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All cities</SelectItem>
                  {cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-sam-slate mb-2">Popular tags</div>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <button key={t.slug} onClick={() => update("tag", tag === t.slug ? "" : t.slug)} data-testid={`filter-tag-${t.slug}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition ${tag === t.slug ? "bg-sam-crimson text-white border-sam-crimson" : "border-border text-sam-slate hover:border-sam-crimson"}`}>
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-xl font-bold text-sam-navy">{loading ? "Searching…" : `${results.length} businesses`}</h1>
              {parent && <Badge variant="secondary" className="capitalize">{activeParent?.name || parent}</Badge>}
              {state && <Badge variant="secondary">{state}</Badge>}
              {city && <Badge variant="secondary" className="capitalize">{cities.find((c) => c.slug === city)?.name || city}</Badge>}
              {state && city && (
                <Link to={`/city/${city}?state=${state}`} data-testid="browse-city-page-link"
                  className="text-xs font-semibold text-sam-crimson hover:underline inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" />View city page
                </Link>
              )}
            </div>
            <Select value={sort} onValueChange={(v) => update("sort", v)}>
              <SelectTrigger className="w-40" data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">Top rated</SelectItem>
                <SelectItem value="reviews">Most reviewed</SelectItem>
                <SelectItem value="name">Name A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="h-64 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>
          ) : results.length === 0 ? (
            <div>
              <div className="rounded-xl border border-dashed border-border p-12 text-center">
                <p className="font-display text-lg text-sam-navy">No businesses match your filters yet.</p>
                <p className="text-sam-slate text-sm mt-1">Try clearing a filter or searching a nearby city.</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate("/browse")}>Clear all</Button>
              </div>
              {nearby.length > 0 && (
                <div className="mt-6" data-testid="browse-nearby">
                  <div className="flex items-center gap-2 font-display font-bold text-sam-navy mb-3"><Compass className="h-5 w-5 text-sam-crimson" />Nearby markets with listings</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {nearby.map((n) => (
                      <button key={`${n.state_abbr}-${n.slug}`} data-testid={`browse-nearby-${n.slug}`}
                        onClick={() => update("city", n.slug)}
                        className="text-left rounded-xl border border-border bg-card p-4 hover:border-sam-crimson hover:shadow-md transition-all">
                        <div className="font-display font-bold text-sam-navy">{n.name}, {n.state_abbr}</div>
                        <div className="text-xs text-sam-slate mt-1">{n.count} {n.count === 1 ? "business" : "businesses"} · {n.distance_mi} mi away</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : metroGroups ? (
            <div className="space-y-8" data-testid="browse-results">
              {metroGroups.map((g) => (
                <div key={g.slug || g.name} data-testid={`metro-group-${g.slug || g.name}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-sam-crimson" />
                    {g.slug ? (
                      <Link to={`/metro/${g.slug}?state=${state}`} data-testid={`metro-group-link-${g.slug}`}
                        className="font-display font-bold text-sam-navy hover:text-sam-crimson transition-colors">{g.name} area</Link>
                    ) : (
                      <h2 className="font-display font-bold text-sam-navy">{g.name} area</h2>
                    )}
                    <span className="text-xs text-sam-slate">{g.items.length} {g.items.length === 1 ? "business" : "businesses"}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {g.items.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="browse-results">
              {results.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
